/**
 * Arac cagrisi kosucusu: sunucudan gelen istegi Pyodide'de calistirip
 * sonucu geri gonderir.
 *
 * ## Neden ayri bir modul
 *
 * `StreamManager` zaten liderlik, sekmeler arasi yayin, devralma ve yeniden
 * dogrulama isini tasiyor. Buraya bir de Pyodide yasam dongusu eklemek onu
 * okunamaz yapardi. Ayrica bu mantik saf: bir istek girer, bir HTTP cagrisi
 * cikar - yani React olmadan test edilebiliyor.
 *
 * ## Zamanlama kisiti: sunucu BEKLIYOR
 *
 * Sunucu tarafinda GPU worker thread'i bu sonucu `threading.Event.wait(20s)`
 * ile bekliyor. Yani burada gecen her saniye, tek GPU'nun BOSTA gectigi bir
 * saniye ve kuyruktaki herkes onu odiyor. Bu yuzden:
 *
 *   * Pyodide tarafinda sinir 18 saniye (sunucunun 20'sinden ONCE bitsin ki
 *     sunucu zaman asimi yerine gercek bir yanit gorsun),
 *   * gonderim hatasinda TEK deneme yapilmiyor - sunucu nasilsa zaman
 *     asimina ugrayip araçsiz devam edecek ve yeniden denemek yalnizca
 *     beklemeyi uzatir.
 *
 * ## Hata her zaman GONDERILIYOR
 *
 * Worker patlasa bile `failed: true` ile bir yanit gidiyor. Sessiz kalmak,
 * sunucunun 20 saniye daha beklemesi demek - yani en kotu senaryo.
 */

import { api, getCsrfToken } from './api'
import { uploadFile } from './attachments'
import type { SandboxFile } from './pyodide'
import type { ToolRequest } from './runStream'

/**
 * Masaustu uygulamasinin preload'u (`desktop/src/preload.js`) yalnizca
 * uygulama icinde `window.hedefDesktop` olarak var; tarayicida TANIMSIZ.
 * Bir araç `local_` ile basliyorsa ve bu yoksa, istek tarayicidan geliyor
 * demektir - sunucuya "yapamadim" diyoruz.
 */
interface HedefDesktop {
  version: string
  platform: string
  runLocalTool(
    name: string,
    args: Record<string, unknown>,
  ): Promise<{ output: string; image?: string; images?: string[] }>
}

declare global {
  interface Window {
    hedefDesktop?: HedefDesktop
  }
}

/** Arayuzun "Python ortami hazirlaniyor…" gostermesi icin. */
type StatusListener = (busy: boolean) => void

const listeners = new Set<StatusListener>()
let activeCalls = 0

export function onToolActivity(listener: StatusListener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function setBusy(delta: number): void {
  activeCalls = Math.max(0, activeCalls + delta)
  for (const listener of listeners) listener(activeCalls > 0)
}

export async function handleToolRequest(runId: string, request: ToolRequest): Promise<void> {
  if (request.tool.startsWith('local_')) {
    await handleLocalTool(runId, request)
    return
  }
  if (request.tool !== 'run_python') {
    // Bilinmeyen arac: sunucuya HEMEN "yapamadim" diyoruz. Sessiz kalmak
    // sunucuyu 20 saniye bekletirdi.
    await send(runId, request.id, '', true)
    return
  }

  setBusy(1)
  try {
    // Lazy import: 13.5 MB'lik Pyodide yalnizca gercekten bir arac cagrisi
    // geldiginde iniyor. Kod calistirmayan konusmalar HICBIR SEY odemiyor -
    // KaTeX ile ayni gerekce.
    const { runPythonInWorker } = await import('./pyodide')
    const files = await fetchAttachments(request.attachments)
    const result = await runPythonInWorker(request.code, files, {
      maxOutputFiles: request.maxOutputFiles,
      maxOutputFileBytes: request.maxOutputFileBytes,
    })
    const outputAttachmentIds = await uploadOutputFiles(result.outputFiles)
    await send(runId, request.id, result.output, result.failed, outputAttachmentIds)
  } catch (error) {
    // Pyodide chunk'i hic yuklenemedi (ag koptu, dosya eksik). Sunucuya
    // yine haber veriyoruz.
    console.error('[arac] calistirilamadi', error)
    await send(runId, request.id, '', true)
  } finally {
    setBusy(-1)
  }
}

/**
 * Sunucunun bildirdigi ekleri (yalnizca id/ad/mime) mevcut kimlik dogrulamali
 * indirme rotasindan ("/api/attachments/{id}") ceker.
 *
 * Bir dosya inememesine ragmen tum calistirma DUSMEZ - kullanicinin diger
 * dosyalarla ilgili kodu hala calissin diye o dosya sessizce atlaniyor. Kod
 * o dosyayi acmaya calisirsa gordugu `FileNotFoundError` NORMAL bir sonuc
 * (model gorup kullaniciya soyleyebilir).
 */
async function fetchAttachments(
  attachments: ToolRequest['attachments'],
): Promise<SandboxFile[]> {
  if (!attachments || attachments.length === 0) return []

  const files = await Promise.all(
    attachments.map(async (attachment): Promise<SandboxFile | null> => {
      try {
        const response = await api(`/api/attachments/${attachment.id}`)
        if (!response.ok) return null
        const data = await response.arrayBuffer()
        return { name: attachment.filename, mime: attachment.mime, data }
      } catch (error) {
        console.error('[arac] ek indirilemedi', error)
        return null
      }
    }),
  )
  return files.filter((f): f is SandboxFile => f !== null)
}

/**
 * Sandbox'in URETTIGI dosyalari mevcut yukleme yolundan (`uploadFile`,
 * composer'in kullandigi AYNI XHR + kota + CSRF hattı) `/api/attachments`e
 * yukler ve olusan id'leri dondurur.
 *
 * Bir dosya (kota/ag) yuklenemezse SESSIZCE atlaniyor - kod ciktisi
 * (`output`) yine gonderiliyor, is dusmuyor.
 */
async function uploadOutputFiles(files: SandboxFile[] | undefined): Promise<string[]> {
  if (!files || files.length === 0) return []

  const csrf = getCsrfToken()
  const ids = await Promise.all(
    files.map(async (file): Promise<string | null> => {
      try {
        const blob = new File([file.data], file.name, { type: file.mime })
        const { promise } = uploadFile(blob, csrf, {
          onProgress: () => {},
          onProcessing: () => {},
        })
        const uploaded = await promise
        return uploaded.id
      } catch (error) {
        console.error('[arac] uretilen dosya yuklenemedi', error)
        return null
      }
    }),
  )
  return ids.filter((id): id is string => id !== null)
}

/**
 * YEREL arac (dosya/kabuk/SQL/ekran...) - masaustu uygulamasinin ana surecine
 * devrediyor.
 *
 * `window.hedefDesktop` YOKSA istek tarayicidan geliyor demektir: sunucuya
 * acikca "bu araç yalnizca masaustu uygulamasinda" diyoruz. Sunucu zaten bu
 * araclari tarayici istemcilerine TANITMIYOR, yani bu normalde olmamali - ama
 * eski bir sunucu veya beklenmedik bir durum icin savunma.
 *
 * Kapsam, onay ve kademe kontrolu ana surecte (`desktop/src/local/`); burada
 * HICBIR karar yok, yalnizca kopru. Ekran goruntusu bir GORSEL donduruyor:
 * onu ek olarak yukleyip modele gosteriyoruz (sohbet ekleriyle ayni yol).
 */
async function handleLocalTool(runId: string, request: ToolRequest): Promise<void> {
  const desktop = window.hedefDesktop
  if (!desktop) {
    // Ayni konusma hem masaustu UYGULAMASINDA hem de bir tarayici sekmesinde
    // aciksa, `tool_request` IKISINE birden gidiyor. Tarayici bu "yapamam"
    // [HATA]'sini HEMEN gonderirse, sunucudaki kopru ILK yaniti kabul ettigi
    // icin (tek kullanimlik) masaustunun GERCEK sonucunu EZEBILIYORDU. Bu
    // yuzden tarayici yanitini birkac saniye GECIKTIRIYORUZ: masaustu istemci
    // varsa o hemen yanitlayip yarisi kazanir; yoksa gecikmeli [HATA] cozulur
    // (kopru, abone-yoklugu grace suresinden ONCE bunu alir).
    await new Promise((resolve) => setTimeout(resolve, 4000))
    await send(
      runId,
      request.id,
      '[HATA] Bu arac yalnizca masaustu uygulamasinda kullanilabilir.',
      true,
    )
    return
  }

  setBusy(1)
  try {
    const result = await desktop.runLocalTool(request.tool, request.args ?? {})

    // Ekran goruntusu: base64 PNG'leri ek olarak yukleyip modele GORSEL ver.
    // Coklu monitor: her ekran AYRI bir gorsel (`images`). Eski istemci tek
    // `image` donduruyor - ikisini de destekliyoruz (geriye donuk uyum).
    const base64ler = result.images ?? (result.image ? [result.image] : [])
    let attachmentIds: string[] = []
    if (base64ler.length) {
      try {
        const coklu = base64ler.length > 1
        const files = base64ler.map((b64, i) => {
          const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
          return {
            name: coklu ? `ekran-${i + 1}.png` : 'ekran-goruntusu.png',
            mime: 'image/png',
            data: bytes.buffer,
          }
        })
        attachmentIds = await uploadOutputFiles(files)
      } catch (error) {
        console.error('[yerel arac] ekran goruntusu yuklenemedi', error)
      }
    }

    // `failed=false` HER zaman: araç ciktisi (bir "[HATA] kapsam disinda" veya
    // "[REDDEDILDI]" olsa bile) KULLANILABILIR ve model GORMELI. Sunucuda
    // `failed=true` kopruye ciktiyi ATTIRIP yerine "zaman asimi" metni koyduruyor
    // (bkz. `tools/bridge.py`), ki bu YALNIZCA altyapi patladiginda dogru -
    // o durum da asagidaki catch'te. Olculen ariza (paylasim
    // `O8O9tasTrbR47Rt95`): model `local_list`i yanlis yolla cagirdi, araç
    // dogru sekilde "kapsam disinda, izinli klasorler: ..." dondu ama bu mesaj
    // "zaman asimina ugradi"ya cevrilince model sasirdi. run_python da ayni
    // gerekceyle Python hatalarini `failed=false` gonderiyor.
    await send(runId, request.id, result.output, false, attachmentIds)
  } catch (error) {
    console.error('[yerel arac] calistirilamadi', error)
    await send(runId, request.id, '[HATA] Yerel arac calistirilamadi.', true)
  } finally {
    setBusy(-1)
  }
}

async function send(
  runId: string,
  requestId: string,
  output: string,
  failed: boolean,
  outputAttachments: string[] = [],
) {
  try {
    await api(`/api/runs/${runId}/tool-result`, {
      method: 'POST',
      body: JSON.stringify({
        request_id: requestId,
        output,
        failed,
        output_attachments: outputAttachments,
      }),
    })
  } catch (error) {
    // Yeniden DENEMIYORUZ: sunucu zaman asimina ugrayip araçsiz devam
    // edecek ve tekrar denemek yalnizca GPU'nun bosta bekledigi sureyi
    // uzatirdi.
    console.error('[arac] sonuc gonderilemedi', error)
  }
}
